// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import { listUsersCustom, getUserCustom, listOrganizationAndMembersCustom } from "../graphql/customQueries";
import { updateUserCustom, updateUserTourFlagsCustom } from "../graphql/customMutations";
import { get } from "aws-amplify/api";
import { listUsers } from "../graphql/queries";
import { updateOrganization } from "../graphql/mutations";
import { AccessRequestService, isAiAnalystRequest } from "../services/accessRequestService";
import { getSubscriptionStatus } from "../api/stripeSubscription";
import { signOut as amplifySignOut } from "aws-amplify/auth";

export const authenticationState = {
  unfetched: "unfetched", //initial state
  unAuthenticated: "unAuthenticated",
  authenticated: "authenticated",
};

const initialState = {
  status: authenticationState.unfetched,
  details: {},
  permissionDetails: {},
  organizations:{ loaded: false,
    data:{}
  },
  aiAnalystAccessRequests: { loaded: false,
    items: []
  },
  // Cached Stripe subscription status, shared by every asset-limit check (chat proposal, Home, group page). status stays null until first fetch resolves; null also means "fail open" while loading or after a failed read.
  subscription: { status: null, loaded: false, loading: false },
};

const client = generateClient();

// Remove this enventually
export const getMeUser = createAsyncThunk(
  "user/getDetails",
  async (id, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getUserCustom,
        variables: { id },
      });

      return data.getUser;
    } catch (error) {
      console.log("Error fetching user details:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Just for profile Pic
export const updateUserRequest =  createAsyncThunk(
  "user/updateUser",
  async (input, thunkAPI) => {
    try {
      // Craig, later get based off of ID, fetch user, get Id, then fetch
      const { data } = await client.graphql({
        query: updateUserCustom,
        variables: { input },
      });
      return data.updateUser;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// New-user tour flags, persisted on the User record so the tour follows the user across devices. Sends only the fields that changed, so the update touches only those attributes — see TourProvider.
export const persistTourFlags = createAsyncThunk<void, { id: string; fields: Record<string, boolean> }>(
  "user/persistTourFlags",
  async ({ id, fields }, thunkAPI) => {
    try {
      await client.graphql({
        query: updateUserTourFlagsCustom,
        variables: { input: { id, ...fields } },
      });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Org picture only — Organization update is limited to the owner and Admins by the model's @auth
export const updateOrganizationPicture = createAsyncThunk<
  { id: string; profilePictureUrl: string },
  { id: string; profilePictureUrl: string },
  { rejectValue: string }
>(
  "user/updateOrganizationPicture",
  async ({ id, profilePictureUrl }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateOrganization,
        variables: { input: { id, profilePictureUrl } },
      });
      return { id, profilePictureUrl: data.updateOrganization.profilePictureUrl };
    } catch (error) {
      return thunkAPI.rejectWithValue((error as Error).message);
    }
  }
);

export const getMyOrganizations = createAsyncThunk(
  "user/getMyOrganizations",
  async (input, thunkAPI) => {

    try {
      // Craig, later get based off of ID, fetch user, get Id, then fetch const { data } = await client.graphql({ query: listUsers, }); console.log("DATA", data)
      let data;
      try {
        ({ data } = await client.graphql({
          query: listOrganizationAndMembersCustom,
        }));
      } catch (error) {
        // AppSync returns a PARTIAL result when a nested field is unauthorized — here another
        // member's `user` on UserOrganizations (a User record is readable only by its owner, Admins,
        // or a viewer in a Cognito group listed in that user's organizationIds). Amplify still throws,
        // with the partial payload on error.data. Use it: an unreadable member is skipped below, and
        // without this a non-admin never gets organizations.loaded = true, which leaves the AI Analyst
        // org-access gate on "loading" (skeleton overlay) forever.
        if (error?.data?.listOrganizations) {
          console.warn("getMyOrganizations: partial result, some members not readable", error.errors);
          data = error.data;
        } else {
          throw error;
        }
      }
      const { listOrganizations, listUserOrganizations  } = data
      const organizationDict = listOrganizations.items.reduce((acc, curr) => {
        return {...acc, [curr.id]: {...curr, members:[]}}
      },{});
      // (semicolon above matters: a line starting with "(" would otherwise be parsed as a call on the reduce result)
      (listUserOrganizations?.items ?? []).forEach(userOrg => {
        // user is null when the record is unreadable (partial result above) or missing
        if (!userOrg?.user) return;
        try{
          organizationDict[userOrg.organizationId].members.push(userOrg.user)
        } catch (e) {
          console.log("couldn't complete user")
        }
      });

      return organizationDict;
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
)

export const getAiAnalystAccessRequests = createAsyncThunk(
  "user/getAiAnalystAccessRequests",
  async (input, thunkAPI) => {
    try {
      // Admin-only (OrgChatAccessPage is behind withAdminAuth). 200 is the page size; listAccessRequests pages through nextToken so nothing is truncated.
      const items = await AccessRequestService.listAccessRequests(200);
      return items.filter(isAiAnalystRequest);
    } catch (error) {
      console.log("error", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
)

// Fetch-once cache: the `condition` skips redundant fetches while one is in flight or already loaded, so consumers can safely dispatch this on every mount. A failed read leaves loaded=false so the next mount retries.
export const fetchSubscriptionStatus = createAsyncThunk(
  "user/fetchSubscriptionStatus",
  async (_, thunkAPI) => {
    try {
      return await getSubscriptionStatus();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { subscription } = getState().user;
      return !subscription?.loaded && !subscription?.loading;
    },
  }
);

const UserSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
    setUserDetails: (state, action) => {
      state.status = authenticationState.authenticated;
      state.details = action.payload;
    },
    logOutUser: (state, action) => {
      state.status = authenticationState.unAuthenticated;
      state.details = {};
      state.permissionDetails = {};
    },
    setTermsAndConditionsStatus: (state, action) => {
      if (state.permissionDetails) {
        console.log("Setting termsAndConditionsReviewed to:", action.payload);
        state.permissionDetails.termsAndConditionsReviewed = action.payload;
      }
    },
    setChatPrivacyPolicyStatus: (state, action) => {
      if (state.permissionDetails) {
        state.permissionDetails.chatPrivacyPolicyAccepted = action.payload;
      }
    },
    setTermsAuditTrail: (state, action) => {
      if (state.permissionDetails) {
        state.permissionDetails.termsAndConditionsAuditTrail = action.payload;
      }
    },
    setPrivacyPolicyAuditTrail: (state, action) => {
      if (state.permissionDetails) {
        state.permissionDetails.privacyPolicyAuditTrail = action.payload;
      }
    },
    setUserConsent: (state, action) => {
      if (state.permissionDetails) {
        state.permissionDetails.userConsent = action.payload;
      }
    },
    // Optimistic new-user tour write — payload: { userId, fields }. Ignored unless the loaded record is that user's, so a write can't land on the wrong user after a switch.
    setTourFlags: (state, action) => {
      const { userId, fields } = action.payload;
      if (state.permissionDetails?.id === userId) {
        Object.assign(state.permissionDetails, fields);
      }
    },
    // Merge updated AI Analyst access requests (e.g. after approval) — payload: AccessRequest[]
    upsertAiAnalystAccessRequests: (state, action) => {
      const updatedById = action.payload.reduce((acc, request) => ({ ...acc, [request.id]: request }), {});
      state.aiAnalystAccessRequests.items = state.aiAnalystAccessRequests.items.map(
        (request) => updatedById[request.id] || request
      );
    },
    // Admin toggle for org AI Analyst access — payload: { organizationId, enabled }
    setOrganizationChatAccess: (state, action) => {
      const { organizationId, enabled } = action.payload;
      if (state.organizations?.data?.[organizationId]) {
        state.organizations.data[organizationId].orgAiAnalystAccess = enabled;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyOrganizations.fulfilled, (state, action) => {
        // newHire state.permissionDetails = action.payload;
        state.organizations.data = action.payload;
        state.organizations.loaded = true
      })
      .addCase(getMyOrganizations.pending, (state, action) => {
        // newHire state.permissionDetails = action.payload;
      }).addCase(getMyOrganizations.rejected, (state, action) => {
        // newHire state.permissionDetails = action.payload;
      })
      .addCase(updateOrganizationPicture.fulfilled, (state, action) => {
        const { id, profilePictureUrl } = action.payload;
        if (state.organizations.data[id]) state.organizations.data[id].profilePictureUrl = profilePictureUrl;
      })
      .addCase(getAiAnalystAccessRequests.fulfilled, (state, action) => {
        state.aiAnalystAccessRequests.items = action.payload;
        state.aiAnalystAccessRequests.loaded = true;
      })
      .addCase(getAiAnalystAccessRequests.rejected, (state, action) => {
        // mark loaded so the page doesn't spin forever; items stay empty
        state.aiAnalystAccessRequests.loaded = true;
      })
      // No case for persistTourFlags: setTourFlags already holds the authoritative value and the mutation only echoes it back. A failed write leaves the optimistic value stood up — the tour just may replay next session.
      .addCase(getMeUser.pending, (state, action) => {
        //newHire
      })
      .addCase(getMeUser.fulfilled, (state, action) => {
        //newHire
        state.permissionDetails = action.payload;
      })
      .addCase(getMeUser.rejected, (state, action) => {
        //newHire
      })
      .addCase(updateUserRequest.fulfilled, (state, action) => {
        // newHire state.permissionDetails = action.payload;
        state.permissionDetails = action.payload;
      })
      .addCase(updateUserRequest.pending, (state, action) => {
        // newHire state.permissionDetails = action.payload;
      }).addCase(updateUserRequest.rejected, (state, action) => {
        // newHire state.permissionDetails = action.payload;
      })
      .addCase(fetchSubscriptionStatus.pending, (state) => {
        state.subscription.loading = true;
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.subscription.status = action.payload;
        state.subscription.loaded = true;
        state.subscription.loading = false;
      })
      .addCase(fetchSubscriptionStatus.rejected, (state) => {
        // Leave loaded=false so a later mount retries; status stays null = fail open.
        state.subscription.loading = false;
      })
  },
});

// Cached Stripe subscription status (null while loading or after a failed read).
export const selectSubscriptionStatus = (state) => state.user.subscription.status;

export const { setUserDetails, logOutUser, setTermsAndConditionsStatus, setChatPrivacyPolicyStatus, setTermsAuditTrail, setPrivacyPolicyAuditTrail, setUserConsent, setTourFlags, setOrganizationChatAccess, upsertAiAnalystAccessRequests } = UserSlice.actions;
// Shared session teardown so the sidebar footer and the settings page sign out identically. Deliberately does NOT dispatch logOutUser: clearing the user in Redux re-renders the nav (admin links vanish, avatar blanks) in the gap before the page goes away. A hard navigation resets the store anyway, so the dispatch bought nothing and cost a visible flash.
export const performSignOut = async ({ posthog }) => {
  await amplifySignOut();
  posthog?.reset(); // writes localStorage synchronously, so it must land before we leave
  window.location.assign("/");
};

export default UserSlice.reducer;
